import { useLazyQuery, useMutation } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';
import { Button } from 'components/Button';
import { SELECT_RETURN_WAREHOUSE } from 'gql/mutations';
import { QUOTE_RETURN_SHIPPING_OPTIONS } from 'gql/queries';
import { ReturnLocation } from 'types/policy';
import {
  Return,
  ReturnStatus,
  SelectedReturnShippingOption,
} from 'types/return';

const selectedWarehouse = (record: Return) =>
  record.meta?.returnWarehouseRouting?.returnLocationId ||
  record.selectedShippingOption?.meta?.returnDestination?.locationId ||
  '';

export const returnWarehouseNeedsStaffAction = (record?: Return) =>
  record?.deflectionOption !== 'KEEP_ITEM_DISCOUNT' &&
  ['MANUAL_SELECTION_REQUIRED', 'ADDRESS_CORRECTION_REQUIRED'].includes(
    record?.meta?.returnWarehouseRouting?.status,
  );

const selection = (option: SelectedReturnShippingOption) => {
  const {
    id,
    source,
    courierServiceId,
    rateId,
    carrier,
    service,
    displayName,
    amount,
    currency,
    meta,
  } = option;
  return {
    id,
    source,
    courierServiceId,
    rateId,
    carrier,
    service,
    displayName,
    amount,
    currency,
    meta,
  };
};

type PendingWarehouseSave = {
  returnId: string;
  storeId: string;
  warehouseId: string;
  updatedAt?: string;
};

export function ReturnWarehouseRoutingPanel({
  record,
  locations,
  onSaved,
  onEditingChange,
}: {
  record: Return;
  locations: ReturnLocation[];
  onSaved: () => Promise<Return | undefined>;
  onEditingChange: (editing: boolean) => void;
}) {
  const savedWarehouseId = selectedWarehouse(record);
  const [warehouseId, setWarehouseId] = useState<string>(
    selectedWarehouse(record),
  );
  const [options, setOptions] = useState<SelectedReturnShippingOption[]>([]);
  const [optionId, setOptionId] = useState('');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const pendingSave = useRef<PendingWarehouseSave | null>(null);
  const [quoteRates] = useLazyQuery(QUOTE_RETURN_SHIPPING_OPTIONS, {
    fetchPolicy: 'network-only',
  });
  const [selectWarehouse] = useMutation(SELECT_RETURN_WAREHOUSE);
  const context = `${record.id}:${record.updatedAt}:${warehouseId}`;
  const currentContext = useRef(context);
  currentContext.current = context;
  const currentReturnId = useRef<string | null>(record.id);
  currentReturnId.current = record.id;
  useEffect(
    () => () => {
      currentContext.current = '';
      currentReturnId.current = null;
    },
    [],
  );
  const acceptedDefectReturn = record.status === ReturnStatus.APPROVED &&
    record.meta?.defectReview?.status === 'APPROVED' &&
    record.meta?.defectResolution?.plan?.status === 'ACCEPTED' &&
    record.meta?.defectResolution?.plan?.returnRequired === true;
  const editable =
    (record.status === ReturnStatus.SUBMITTED || acceptedDefectReturn) &&
    !record.shippingId &&
    !record.labelUrl &&
    !record.trackingNumber &&
    !record.labelSource &&
    !record.shopifyReturnId &&
    !record.shopifyReturnSyncStatus &&
    !record.shopifyReturnSyncedAt &&
    !record.settlementStatus &&
    !record.settlementType &&
    !record.settlementTriggeredAt &&
    !record.settlementCompletedAt;
  const method = options.find((option) => option.id === optionId);
  const changed =
    warehouseId !== selectedWarehouse(record) ||
    Boolean(
      method &&
        (method.source !== record.selectedShippingOption?.source ||
          method.courierServiceId !==
            record.selectedShippingOption?.courierServiceId),
    );
  const saveConfirmationPending = pendingSave.current !== null;

  useEffect(() => {
    setWarehouseId(savedWarehouseId);
    setOptions([]);
    setOptionId('');
    // Mutation cache writes can arrive before the awaited save finishes.
    if (pendingSave.current?.returnId !== record.id) {
      pendingSave.current = null;
      setError('');
      setSaved(false);
      setSaving(false);
    }
    setChecking(false);
  }, [record.id, record.updatedAt, savedWarehouseId]);
  useEffect(() => {
    onEditingChange(
      editable && (changed || saving || saved || saveConfirmationPending),
    );
    return () => onEditingChange(false);
  }, [
    editable,
    changed,
    saving,
    saved,
    saveConfirmationPending,
    onEditingChange,
  ]);

  const refreshSavedWarehouse = async () => {
    const expected = pendingSave.current;
    if (!expected?.updatedAt) return;
    setSaving(true);
    setError('');
    try {
      const refreshed = await onSaved();
      if (
        currentReturnId.current !== expected.returnId ||
        pendingSave.current !== expected
      ) return;
      if (
        refreshed?.id !== expected.returnId ||
        refreshed.storeId !== expected.storeId ||
        refreshed.updatedAt !== expected.updatedAt ||
        selectedWarehouse(refreshed) !== expected.warehouseId
      ) {
        throw new Error(
          'The refreshed return did not confirm the saved warehouse version. Refresh the current return before approving.',
        );
      }
      setSaving(false);
      pendingSave.current = null;
      setSaved(false);
    } catch (error) {
      if (
        currentReturnId.current === expected.returnId &&
        pendingSave.current === expected
      )
        setError(
          error instanceof Error
            ? error.message
            : 'The saved warehouse could not be verified. Refresh the current return before approving.',
        );
    } finally {
      if (
        currentReturnId.current === expected.returnId &&
        pendingSave.current === expected
      ) setSaving(false);
    }
  };

  const checkRates = async () => {
    const requestContext = currentContext.current;
    setChecking(true);
    setError('');
    setOptions([]);
    try {
      const response = await quoteRates({
        variables: {
          input: {
            orderId: record.orderId,
            storeId: record.storeId,
            excludeReturnId: record.id,
            returnLocationId: warehouseId,
            items: record.returnItems.map((item) => ({
              id: item.orderItemId || item.orderItem.id,
              quantity: item.quantity,
            })),
          },
        },
      });
      if (currentContext.current !== requestContext) return;
      const quote = response.data?.quoteReturnShippingOptions;
      if (
        quote?.storeId !== record.storeId ||
        quote?.orderId !== record.orderId
      )
        throw new Error(
          'Rates did not match this return. Refresh the return and try again.',
        );
      const available = (quote.options || []).filter(
        (option: SelectedReturnShippingOption) =>
          option.customerVisible !== false &&
          option.meta?.returnDestination?.locationId === warehouseId,
      );
      setOptions(available);
      const currentMethod = available.find(
        (option: SelectedReturnShippingOption) =>
          option.source === record.selectedShippingOption?.source &&
          option.courierServiceId ===
            record.selectedShippingOption?.courierServiceId,
      );
      setOptionId(currentMethod?.id || available[0]?.id || '');
      if (!available.length)
        setError(
          quote.errorMessage ||
            'No configured return methods are currently available for this warehouse.',
        );
    } catch (error) {
      if (currentContext.current === requestContext)
        setError(
          error instanceof Error
            ? error.message
            : 'Warehouse rates could not be loaded. Try again.',
        );
    } finally {
      if (currentContext.current === requestContext) setChecking(false);
    }
  };
  const saveWarehouse = async () => {
    if (
      !editable ||
      !warehouseId ||
      saving ||
      checking ||
      saved ||
      pendingSave.current
    ) return;
    setSaving(true);
    setError('');
    const requestReturnId = record.id;
    const expected: PendingWarehouseSave = {
      returnId: record.id,
      storeId: record.storeId,
      warehouseId,
    };
    pendingSave.current = expected;
    try {
      const option = options.find((entry) => entry.id === optionId);
      const response = await selectWarehouse({
        variables: {
          input: {
            returnId: record.id,
            storeId: record.storeId,
            returnLocationId: warehouseId,
            expectedUpdatedAt: record.updatedAt,
            ...(option ? { selectedShippingOption: selection(option) } : {}),
          },
        },
      });
      if (
        currentReturnId.current !== requestReturnId ||
        pendingSave.current !== expected
      ) return;
      setSaved(true);
      const persisted = response.data?.selectReturnWarehouse;
      if (
        persisted?.id !== expected.returnId ||
        !persisted.updatedAt ||
        persisted.updatedAt === record.updatedAt ||
        selectedWarehouse(persisted) !== expected.warehouseId
      ) {
        throw new Error(
          'The warehouse save could not be confirmed. Refresh the current return before approving.',
        );
      }
      expected.updatedAt = persisted.updatedAt;
      await refreshSavedWarehouse();
    } catch (error) {
      if (
        currentReturnId.current !== requestReturnId ||
        pendingSave.current !== expected
      ) return;
      setError(
        error instanceof Error
          ? error.message
          : 'The warehouse selection could not be saved. Refresh and try again.',
      );
    } finally {
      if (
        currentReturnId.current === requestReturnId &&
        pendingSave.current === expected
      ) setSaving(false);
    }
  };
  const chosen = locations.find(
    (location) => location.id === selectedWarehouse(record),
  );
  if (record.deflectionOption === 'KEEP_ITEM_DISCOUNT') return null;
  return (
    <section className="mb-6 space-y-4 rounded-xl border border-gray-200 bg-white p-5">
      <h2 className="text-base font-semibold text-gray-900">
        Return warehouse
      </h2>
      {record.meta?.returnWarehouseRouting?.reason &&
        returnWarehouseNeedsStaffAction(record) && (
          <p className="text-sm text-amber-800">
            {record.meta.returnWarehouseRouting.reason}
          </p>
        )}
      {!editable ? (
        <>
          <p className="text-sm text-gray-700">
            {chosen?.label || 'Saved return destination'}
          </p>
          <p className="text-xs text-gray-500">
            Warehouse changes are available before approval and label creation.
          </p>
        </>
      ) : (
        <>
          <p className="text-sm text-gray-600">
            Review the destination before approving. Checking rates and saving a
            warehouse do not purchase a label.
          </p>
          <label className="block text-sm font-medium text-gray-700">
            Return warehouse
            <select
              aria-label="Return warehouse"
              value={warehouseId}
              disabled={saving || saved || saveConfirmationPending}
              onChange={(event) => {
                setWarehouseId(event.target.value);
                setOptions([]);
                setOptionId('');
                setError('');
                setChecking(false);
              }}
              className="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
            >
              <option value="">Select a return warehouse</option>
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {[
                    location.label,
                    location.city,
                    location.country_alpha2 || location.country,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </option>
              ))}
            </select>
          </label>
          {options.length > 0 && (
            <label className="block text-sm font-medium text-gray-700">
              Return method for selected warehouse
              <select
                value={optionId}
                onChange={(event) => setOptionId(event.target.value)}
                disabled={saving || saved || saveConfirmationPending}
                className="mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
              >
                {options.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.displayName || option.service} · {option.currency}{' '}
                    {option.amount}
                  </option>
                ))}
              </select>
            </label>
          )}
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outlined"
              color="none"
              disabled={
                !warehouseId || checking || saving || saved || saveConfirmationPending
              }
              isLoading={checking}
              onClick={checkRates}
            >
              Check warehouse rates
            </Button>
            <Button
              disabled={
                !warehouseId ||
                checking ||
                saving ||
                saved ||
                saveConfirmationPending ||
                (!changed && !options.length)
              }
              isLoading={saving}
              onClick={saveWarehouse}
            >
              Save warehouse selection
            </Button>
          </div>
          {saved && (
            <p role="status" className="text-sm text-green-700">
              Warehouse selection saved. Refreshing the current return before
              approval.
            </p>
          )}
          {saved && error && pendingSave.current?.updatedAt && (
            <Button
              variant="outlined"
              color="none"
              disabled={saving}
              isLoading={saving}
              onClick={refreshSavedWarehouse}
            >
              Refresh current return
            </Button>
          )}
        </>
      )}
      {error && (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}
    </section>
  );
}
